import React from 'react';
import { Search, ArrowRight, DollarSign, Globe, BarChart3, Clock, LayoutGrid } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEO from '../SEO';

const ScholarshipFinder: React.FC = () => {
    const navigate = useNavigate();

    const handleActionClick = () => {
        navigate('/eligibility?service=admissions');
    };

    return (
        <div className="bg-white min-h-screen pt-32 pb-20">
            <SEO
                title="Scholarship Finder | NexOverseas"
                description="Intelligent financial aid discovery tailored to your academic profile. Access a continuously updated library of global scholarships and bursaries."
                canonical="/admissions/scholarship-finder"
            />

            <div className="bg-[#009B8F] text-white py-20 mb-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
                    <span className="text-white/80 font-bold tracking-widest text-xs uppercase mb-4 block">Admissions Suite</span>
                    <h1 className="text-4xl md:text-6xl font-serif mb-6">Scholarship Finder</h1>
                    <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                        Intelligent financial aid discovery tailored to your academic profile.
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start mb-24">
                    <div className="prose prose-lg text-slate-600 max-w-none">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 bg-[#009B8F]/10 rounded-full">
                                <DollarSign className="text-[#009B8F] w-8 h-8" />
                            </div>
                            <h2 className="text-3xl font-serif text-[#011E26] m-0">Unlock Global Opportunities</h2>
                        </div>

                        <p className="leading-relaxed mb-6">
                            Finding international scholarships is one of the most time‑consuming and confusing parts of
                            studying abroad. Funding information is scattered across government portals, university
                            websites, and private organisations and most students never discover the opportunities they
                            are eligible for.
                        </p>

                        <p className="leading-relaxed mb-6">
                            NexOverseas simplifies this process with a Data‑Driven Scholarship Finder
                            that centralises global funding options and aligns them with your academic background and
                            study goals.
                            Students complete a single profile, and the platform organises relevant scholarships based
                            on nationality, academic history, destination country, and program level.
                        </p>

                        <p className="leading-relaxed mb-8">
                            Instead of relying on guesswork or incomplete advice, you receive a personalised list of
                            scholarships that match your eligibility along with clear deadlines, requirements, and
                            application steps. This helps you focus your time on high‑value opportunities and maximise
                            your financial support potential.
                        </p>

                        <button
                            onClick={handleActionClick}
                            className="group inline-flex items-center gap-3 bg-[#009B8F] text-white px-10 py-5 font-bold uppercase tracking-widest text-xs hover:bg-[#011E26] transition-all duration-300 rounded-sm shadow-2xl"
                        >
                            Run a check with AI <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="bg-slate-50 p-10 rounded-sm border border-slate-100 shadow-sm relative">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-[#009B8F]/5 rounded-bl-full -z-10"></div>
                        <h3 className="text-xl font-serif text-[#011E26] mb-10">How the Scholarship Finder Works</h3>

                        <div className="space-y-10">
                            {[
                                { icon: <Search size={22} />, title: "1. Profile-Based Matching", desc: "Your academic background and preferences are mapped against curated funding criteria to identify matches." },
                                { icon: <Globe size={22} />, title: "2. Updated Funding Library", desc: "Our team maintains a structured database of government scholarships, university bursaries, and private grants." },
                                { icon: <BarChart3 size={22} />, title: "3. Prioritised Recommendations", desc: "Scholarships are ranked based on eligibility strength, competitiveness, and alignment with your goals." },
                                { icon: <Clock size={22} />, title: "4. Deadline Tracking & Alerts", desc: "The platform highlights upcoming deadlines and required documents so you never miss an opportunity." },
                                { icon: <LayoutGrid size={22} />, title: "5. Centralised Dashboard", desc: "All eligible funding options are displayed in one organised view, making it easy to compare and plan." }
                            ].map((item, i) => (
                                <div key={i} className="flex gap-5 group">
                                    <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center text-[#009B8F] border border-slate-100 group-hover:bg-[#009B8F] group-hover:text-white transition-colors duration-300">
                                        {item.icon}
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-bold text-[#011E26] text-sm uppercase tracking-wider mb-2">{item.title}</h4>
                                        <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="bg-[#011E26] rounded-sm p-12 text-white overflow-hidden relative">
                    <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl font-serif mb-6 text-[#009B8F]">Real ROI Focus</h2>
                            <p className="text-slate-400 leading-relaxed mb-6">
                                We don't just find you money; we find you the right investment. Our team continuously updates the database with verified government grants and university bursaries across 20+ countries.
                            </p>
                            <div className="flex gap-4">
                                <div className="px-4 py-2 bg-white/5 rounded border border-white/10 text-xs font-bold uppercase tracking-widest text-[#009B8F]">20,000+ Students Helped</div>
                                <div className="px-4 py-2 bg-white/5 rounded border border-white/10 text-xs font-bold uppercase tracking-widest text-[#009B8F]">£10M+ Funding Secured</div>
                            </div>
                        </div>
                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1546410531-bb4caa6b424d?auto=format&fit=crop&q=80&w=800"
                                alt="University graduation"
                                className="rounded shadow-2xl opacity-80"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScholarshipFinder;
